import { Component, Inject, OnDestroy } from '@angular/core'
import { MapService } from 'src/app/core/services/map.service'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AppPaths } from 'src/app/app-routing.module'
import { LayerRoadSegmentService } from '../../../shared/layers/layer-road-segment.service'
import { LayerWeatherService } from '../../../shared/layers/layer-weather.service'

import { getMetricFromMap } from '../../enums/bwc-metrics.enum'

import { Observable, Subject, debounceTime, interval, takeUntil } from 'rxjs'

import { DataRoadSegmentService } from 'src/app/shared/services/data-road-segment.service'
import { DataWeatherPredictionService } from 'src/app/shared/services/data-weather-prediction.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import {
  provider4FogLayerProvider,
  provider4HRLayerProvider,
} from 'src/app/shared/providers/layer-service-provider'
import { LayerRawEventsService } from 'src/app/shared/layers/layer-raw-events.service'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import { LayerWebcamService } from 'src/app/shared/layers/layer-static-webcams.service'
import { DataWebcamsService } from 'src/app/shared/services/data-webcams.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DataProviderInfoService } from 'src/app/shared/services/data-provider-info.service'
import { ProviderInfo } from 'src/app/obstacle/model/provider-info.model'

@Component({
  selector: 'app-bad-weather-condition-page',
  templateUrl: './bad-weather-condition-page.component.html',
  standalone: false,
  providers: [
    LayerRoadSegmentService,
    LayerWeatherService,
    LayerWebcamService,
    DataRoadSegmentService,
    DataWeatherPredictionService,
    DataWebcamsService,
    DataProviderInfoService,
    { provide: 'metrics', useValue: getMetricFromMap },
    provider4HRLayerProvider,
    provider4FogLayerProvider,
  ],
})
export class BadWeatherConditionPageComponent implements OnDestroy {
  private stopTimer = new Subject<void>()
  public Permissions = Permissions
  public isWeatherPredictionLayerVisible = false
  public isRoadMetricLayerVisible = false
  public isProvider4HeavyRainLayerVisible = false
  public isProvider4FogLayerVisible = false
  public isLayerSelectionVisible = false
  public isWebcamLayerVisible = false
  public isInLiveMode = false
  private autoRefreshIntervalInMs = 0
  public displayRefreshInterval = 0
  public layerIds = Layers
  public currentTimestamp: Date | undefined
  public currentTimespan: number | undefined
  public isHistoryVisible = false
  public isLegendVisible = false
  public ProviderInfo$!: Observable<ProviderInfo>

  constructor(
    private mapService: MapService,
    private appState: AppStateService,
    private weatherLayerService: LayerWeatherService,
    private roadLayerService: LayerRoadSegmentService,
    private webcamsLayerService: LayerWebcamService,
    @Inject('provider4HRLayer') public provider4HRLayerService: LayerRawEventsService,
    @Inject('provider4FogLayer') public provider4FogLayerService: LayerRawEventsService,
    private dataProviderInfoService: DataProviderInfoService,
  ) {
    this.mapService.activateGeoLocationSearch()
    this.mapService.setPosition(
      this.appState.getState().lat,
      this.appState.getState().lon,
      this.appState.getState().zoom,
    )
    if (this.appState.getState().historyEnabled) {
      this.isHistoryVisible = true
    }
    this.updateQueryParameters()
    this.mapService.mapViewChanged
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.updateQueryParameters()
      })

    this.evaluateQueryParameters()
    this.appState.stateChanged.pipe(takeUntilDestroyed()).subscribe(() => {
      this.evaluateQueryParameters()
    })
  }

  async ngOnInit() {
    this.ProviderInfo$ = await this.dataProviderInfoService.loadProviderInfo()
  }

  ngOnDestroy() {
    this.mapService.deactivateGeoLocationSearch()
    this.stopTimer.next()
    this.stopTimer.complete()
  }

  startLiveMode() {
    this.isInLiveMode = true
    this.stopTimer = new Subject<void>()
    interval(this.autoRefreshIntervalInMs)
      .pipe(takeUntil(this.stopTimer))
      .subscribe(() => {
        this.roadLayerService.reload()
        this.weatherLayerService.reload()
        this.provider4FogLayerService.reload()
        this.provider4HRLayerService.reload()
      })
  }

  stopLiveMode() {
    this.isInLiveMode = false
    this.stopTimer.next()
  }

  toggleHistory = () => {
    if (!this.isHistoryVisible) {
      this.setLiveModeRefreshInterval(0)
    }
    this.isHistoryVisible = !this.isHistoryVisible
    this.updateQueryParameters()
  }

  toggleLegend = () => {
    this.isLegendVisible = !this.isLegendVisible
  }

  toggleWebcamLayer = () => {
    this.isWebcamLayerVisible = !this.isWebcamLayerVisible
    const layers = this.appState
      .selectLayers()
      .filter((layer) => !layer.includes(Layers.Webcams.name))

    if (this.isWebcamLayerVisible) {
      layers.push(Layers.Webcams.name)
    }

    this.appState.setState(AppPaths.BadWeatherCondition, {
      layers: layers?.toString(),
    })
  }

  handleHistoryClosed = () => {
    this.setLiveModeRefreshInterval(0)
    this.isHistoryVisible = false
    this.updateQueryParameters()
  }

  setLiveModeRefreshInterval(seconds: number) {
    this.autoRefreshIntervalInMs = seconds * 1000
    this.displayRefreshInterval = seconds
    this.stopLiveMode()
    if (this.autoRefreshIntervalInMs > 0) {
      this.startLiveMode()
    }
  }

  evaluateQueryParameters() {
    if (this.appState.getState().historyEnabled) {
      this.isHistoryVisible = true
    }
    if (this.appState.getState().layers?.includes(Layers.RoadSegment.name)) {
      this.isRoadMetricLayerVisible = true
    }

    if (this.appState.getState().layers?.includes(Layers.WeatherPrediction.name)) {
      this.isWeatherPredictionLayerVisible = true
    }

    if (this.appState.getState().layers?.includes(Layers.Provider4Fog.name)) {
      this.isProvider4FogLayerVisible = true
    }
    if (this.appState.getState().layers?.includes(Layers.Provider4HeavyRain.name)) {
      this.isProvider4HeavyRainLayerVisible = true
    }

    if (this.appState.getState().layers?.includes(Layers.Webcams.name)) {
      this.isWebcamLayerVisible = true
    }
  }

  updateQueryParameters() {
    this.appState.setState(AppPaths.BadWeatherCondition, {
      lat: this.mapService.map.getCenter().lat,
      lon: this.mapService.map.getCenter().lng,
      zoom: this.mapService.map.getZoom(),
      historyEnabled: this.isHistoryVisible ? true : undefined,
    })
  }
}
